import {
  DefaultNodeTypes,
  SerializedBlockNode,
} from '@payloadcms/richtext-lexical';
import React, { Fragment, JSX } from 'react';

import { AccordionBlock } from '@/blocks/AccordionBlock/Component';
import { ButtonGroupBlock } from '@/blocks/ButtonGroupBlock/Component';
import { CodeBlock } from '@/blocks/CodeBlock/Component';
import { EmbedBlock } from '@/blocks/EmbedBlock/Component';
import { IntroductoryParagraphBlock } from '@/blocks/IntroductoryParagraphBlock/Component';
import { MediaBlock } from '@/blocks/MediaBlock/Component';
import { TextElementBlock } from '@/blocks/TextElementBlock/Component';
import { CMSLink } from '@/components/Link';
import type { MediaBlock as MediaBlockProps } from '@/payload-types';
import type { ButtonGroupBlock as ButtonGroupBlockProps } from '@/payload-types';
import type { EmbedBlock as EmbedBlockProps } from '@/payload-types';
import type { TextElementBlock as TextElementBlockProps } from '@/payload-types';
import type { AccordionBlock as AccordionBlockProps } from '@/payload-types';
import type { IntroductoryParagraphBlock as IntroductoryParagraphBlockProps } from '@/payload-types';
import type { CodeBlock as CodeBlockProps } from '@/payload-types';

import {
  IS_BOLD,
  IS_CODE,
  IS_ITALIC,
  IS_STRIKETHROUGH,
  IS_SUBSCRIPT,
  IS_SUPERSCRIPT,
  IS_UNDERLINE,
} from './nodeFormat';

export type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<MediaBlockProps>
  | SerializedBlockNode<ButtonGroupBlockProps>
  | SerializedBlockNode<TextElementBlockProps>
  | SerializedBlockNode<CodeBlockProps>
  | SerializedBlockNode<AccordionBlockProps>
  | SerializedBlockNode<IntroductoryParagraphBlockProps>
  | SerializedBlockNode<EmbedBlockProps>;

type Props = {
  nodes: NodeTypes[];
};

export function serializeLexical({ nodes }: Props): JSX.Element {
  return (
    <Fragment>
      {nodes?.map((node, index): JSX.Element | null => {
        if (node == null) {
          return null;
        }

        if (node.type === 'text') {
          let text = <React.Fragment key={index}>{node.text}</React.Fragment>;
          if (node.format & IS_BOLD) {
            text = <strong key={index}>{text}</strong>;
          }
          if (node.format & IS_ITALIC) {
            text = <em key={index}>{text}</em>;
          }
          if (node.format & IS_STRIKETHROUGH) {
            text = (
              <span key={index} style={{ textDecoration: 'line-through' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_UNDERLINE) {
            text = (
              <span key={index} style={{ textDecoration: 'underline' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_CODE) {
            text = <code key={index}>{node.text}</code>;
          }
          if (node.format & IS_SUBSCRIPT) {
            text = <sub key={index}>{text}</sub>;
          }
          if (node.format & IS_SUPERSCRIPT) {
            text = <sup key={index}>{text}</sup>;
          }

          return text;
        }

        // NOTE: Hacky fix for
        // https://github.com/facebook/lexical/blob/d10c4e6e55261b2fdd7d1845aed46151d0f06a8c/packages/lexical-list/src/LexicalListItemNode.ts#L133
        // which does not return checked: false (only true - i.e. there is no prop for false)
        const serializedChildrenFn = (node: NodeTypes): JSX.Element | null => {
          if (node.children == null) {
            return null;
          } else {
            if (node?.type === 'list' && node?.listType === 'check') {
              for (const item of node.children) {
                if ('checked' in item) {
                  if (!item?.checked) {
                    item.checked = false;
                  }
                }
              }
            }
            return serializeLexical({ nodes: node.children as NodeTypes[] });
          }
        };

        const serializedChildren =
          'children' in node ? serializedChildrenFn(node) : '';

        if (node.type === 'block') {
          const block = node.fields;

          const blockType = block?.blockType;

          if (!block || !blockType) {
            return null;
          }

          switch (blockType) {
            case 'buttonGroup':
              return <ButtonGroupBlock key={index} {...(block as any)} />;
            case 'media':
              return <MediaBlock key={index} {...(block as any)} />;
            case 'textElement':
              return <TextElementBlock key={index} {...(block as any)} />;
            case 'accordion':
              return <AccordionBlock key={index} {...(block as any)} />;
            case 'code':
              return <CodeBlock key={index} {...(block as any)} />;
            case 'introductory-paragraph':
              return (
                <IntroductoryParagraphBlock key={index} {...(block as any)} />
              );
            case 'embed-block':
              return (
                <EmbedBlock key={index} {...(block as any)} gutter={false} />
              );
            default:
              return null;
          }
        } else {
          switch (node.type) {
            case 'linebreak': {
              return <br key={index} />;
            }
            case 'paragraph': {
              // Check if children is empty, if so return <p><br /></p>
              if (
                !(
                  (serializedChildren as JSX.Element).props?.children &&
                  (serializedChildren as JSX.Element).props?.children.length > 0
                )
              ) {
                return (
                  <p key={index}>
                    <br />
                  </p>
                );
              }
              return <p key={index}>{serializedChildren}</p>;
            }
            case 'heading': {
              const Tag = node?.tag;
              return <Tag key={index}>{serializedChildren}</Tag>;
            }
            case 'list': {
              const Tag = node?.tag;
              return <Tag key={index}>{serializedChildren}</Tag>;
            }
            case 'listitem': {
              if (node?.checked != null) {
                return (
                  <li
                    aria-checked={node.checked ? 'true' : 'false'}
                    className={` ${node.checked ? '' : ''}`}
                    key={index}
                    role='checkbox'
                    tabIndex={-1}
                    value={node?.value}
                  >
                    {serializedChildren}
                  </li>
                );
              } else {
                return (
                  <li key={index} value={node?.value}>
                    {serializedChildren}
                  </li>
                );
              }
            }
            case 'quote': {
              return <blockquote key={index}>{serializedChildren}</blockquote>;
            }
            case 'link': {
              const fields = node.fields;

              return (
                <CMSLink
                  key={index}
                  newTab={Boolean(fields?.newTab)}
                  reference={fields.doc as any}
                  type={fields.linkType === 'internal' ? 'reference' : 'custom'}
                  url={fields.url}
                >
                  {serializedChildren}
                </CMSLink>
              );
            }

            default:
              return null;
          }
        }
      })}
    </Fragment>
  );
}
